import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { dispatchService, ItemScanResult, DispatchItem, CreateDispatchRequest } from '../services/dispatchService';
import { Scanner } from '@yudiel/react-qr-scanner';
import ReusableSidebar from '../components/public/ReusableSidebar/ReusableSidebar';
import {
  XMarkIcon,
  QrCodeIcon,
  TruckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  PencilIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface DispatchScreenProps {}

const DispatchScreen: React.FC<DispatchScreenProps> = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [showNewDispatchForm, setShowNewDispatchForm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scannedItems, setScannedItems] = useState<DispatchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    vehicleNo: '',
    driverId: '',
    driverName: '',
    driverContact: '',
    dispatchDate: new Date().toISOString().slice(0, 16),
    remarks: '',
  });

  const handleStartNewDispatch = () => {
    setShowNewDispatchForm(true);
    setScannedItems([]);
    setFormData({
      vehicleNo: '',
      driverId: '',
      driverName: '',
      driverContact: '',
      dispatchDate: new Date().toISOString().slice(0, 16),
      remarks: '',
    });
  };

  const handleStartScanning = () => {
    if (!formData.vehicleNo ) {
      toast.error('Please fill in Vehicle No');
      return;
    }
    setShowNewDispatchForm(false);
    setShowScanner(true);
    setScanning(false); // Start with scanning false, will be enabled when scanner is ready
  };

  const handleScan = async (result: any) => {
    // Extract QR code value from result (can be string or array)
    let qrCode: string = '';
    
    if (typeof result === 'string') {
      qrCode = result;
    } else if (Array.isArray(result) && result.length > 0) {
      qrCode = result[0]?.rawValue || result[0] || '';
    } else if (result?.rawValue) {
      qrCode = result.rawValue;
    } else {
      console.error('Unexpected scan result format:', result);
      return;
    }

    // Validate QR code value
    if (!qrCode || qrCode.trim() === '') {
      console.error('Empty QR code detected');
      return;
    }

    // Prevent concurrent scans
    if (scanning) {
      console.log('Scan already in progress, ignoring...');
      return;
    }

    console.log('Scanning QR code:', qrCode);
    setScanning(true);
    
    try {
      const response = await dispatchService.getItemByCode(qrCode);
      console.log('API response:', response);
      
      if (response.success && response.data) {
        const { item, readyForDispatch, message } = response.data;

        if (!readyForDispatch) {
          // Show detailed error message with stage completion info
          const errorMsg = message || 
            (item.stage_completion 
              ? `Item ${item.qr_code} is not ready for dispatch. Status: ${item.status}. Completed stages: ${item.stage_completion.completed_stages}/${item.stage_completion.total_stages}`
              : `Item ${item.qr_code} is not ready for dispatch. Status must be "Completed" and all stages must be completed.`);
          toast.error(errorMsg);
          setScanning(false);
          return;
        }

        // Check if item already scanned
        if (scannedItems.some(si => si.id === item.id)) {
          toast.error('Item already scanned');
          setScanning(false);
          return;
        }

        // Add to scanned items
        const dispatchItem: DispatchItem = {
          id: item.id,
          qr_code: item.qr_code,
          item_type: item.type,
          quantity: item.quantity,
          product_name: item.product_name,
          order_number: item.order_number,
          customer_name: item.customer_name,
          status: 'pending',
        };

        setScannedItems(prev => [...prev, dispatchItem]);
        toast.success(`Item ${item.qr_code} added to dispatch`);
      } else {
        const errorMsg = response.error?.message || 'Failed to scan item';
        console.error('Scan error:', errorMsg);
        toast.error(errorMsg);
      }
    } catch (error: any) {
      console.error('Scan exception:', error);
      toast.error(error.message || 'Failed to scan item');
    } finally {
      // Add a small delay before allowing next scan to prevent rapid re-scans
      setTimeout(() => {
        setScanning(false);
      }, 1000);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    setScannedItems(prev => prev.filter(item => item.id !== itemId));
    toast.success('Item removed');
  };

  const handleSubmitDispatch = async () => {
    if (scannedItems.length === 0) {
      toast.error('Please scan at least one item');
      return;
    }

    setSubmitting(true);
    try {
      const payload: CreateDispatchRequest = {
        vehicleNo: formData.vehicleNo,
        driverId: formData.driverId,
        driverName: formData.driverName,
        driverContact: formData.driverContact,
        items: scannedItems.map(item => item.id),
        dispatchDate: formData.dispatchDate,
        remarks: formData.remarks,
      };

      const response = await dispatchService.createDispatch(payload);

      if (response.success) {
        toast.success('Dispatch created successfully!');
        setShowScanner(false);
        setScannedItems([]);
        setFormData({
          vehicleNo: '',
          driverId: '',
          driverName: '',
          driverContact: '',
          dispatchDate: new Date().toISOString().slice(0, 16),
          remarks: '',
        });
      } else {
        toast.error(response.error?.message || 'Failed to create dispatch');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create dispatch');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Dispatch Management</h1>
          <p className="text-gray-600 mt-2">Supervisor Dashboard</p>
        </div>

        {/* Start New Dispatch Button */}
        <div className="mb-6">
          <button
            onClick={handleStartNewDispatch}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Start New Dispatch
          </button>
        </div>

        {/* New Dispatch Form Sidebar */}
        <ReusableSidebar
          isOpen={showNewDispatchForm}
          onClose={() => setShowNewDispatchForm(false)}
          panelId="new-dispatch-form"
          title="New Dispatch"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle No <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.vehicleNo}
                onChange={(e) => setFormData({ ...formData, vehicleNo: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Enter vehicle number"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Driver ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.driverId}
                onChange={(e) => setFormData({ ...formData, driverId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Enter driver ID (UUID)"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Driver Name
              </label>
              <input
                type="text"
                value={formData.driverName}
                onChange={(e) => setFormData({ ...formData, driverName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Enter driver name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Driver Contact
              </label>
              <input
                type="text"
                value={formData.driverContact}
                onChange={(e) => setFormData({ ...formData, driverContact: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Enter driver contact"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dispatch Date/Time
              </label>
              <input
                type="datetime-local"
                value={formData.dispatchDate}
                onChange={(e) => setFormData({ ...formData, dispatchDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks
              </label>
              <textarea
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Optional remarks"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setShowNewDispatchForm(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleStartScanning}
                disabled={!formData.vehicleNo || !formData.driverId}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md ${
                  !formData.vehicleNo || !formData.driverId
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                Start Scanning
              </button>
            </div>
          </div>
        </ReusableSidebar>

        {/* Scanner Sidebar */}
        <ReusableSidebar
          isOpen={showScanner}
          onClose={() => {
            setShowScanner(false);
            setScanning(false);
          }}
          panelId="item-scanner"
          title="Scan Items for Dispatch"
        >
          <div className="space-y-4">
            {/* Vehicle Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TruckIcon className="w-5 h-5 text-blue-600" />
                <span className="font-medium">Vehicle: {formData.vehicleNo}</span>
              </div>
              <div className="text-sm text-gray-600">
                Driver: {formData.driverName || formData.driverId}
              </div>
            </div>

            {/* Scanner Section */}
            <div>
              <div className="aspect-square w-full max-w-md mx-auto rounded-lg overflow-hidden bg-gray-100 mb-4">
                {showScanner ? (
                  <Scanner
                    onScan={handleScan}
                    onError={(error) => {
                      console.error('Scanner error:', error);
                      toast.error('Scanner error occurred. Please check camera permissions.');
                      setScanning(false);
                    }}
                    constraints={{
                      facingMode: 'environment',
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <QrCodeIcon className="w-16 h-16 text-gray-400" />
                  </div>
                )}
              </div>

              <p className="text-center text-sm text-gray-600 mb-4">
                {scanning ? 'Processing scan...' : 'Point your camera at a QR code to scan an item'}
              </p>
            </div>

            {/* Scanned Items List */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Scanned Items ({scannedItems.length})</h3>
              {scannedItems.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No items scanned yet</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {scannedItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-sm">{item.qr_code}</div>
                        <div className="text-xs text-gray-600">
                          {item.customer_name} • {item.product_name} • Qty: {item.quantity}
                        </div>
                        <div className="text-xs text-gray-500">Order: {item.order_number}</div>
                      </div>
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4 border-t">
              <button
                onClick={() => {
                  setShowScanner(false);
                  setScanning(false);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitDispatch}
                disabled={scannedItems.length === 0 || submitting}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md ${
                  scannedItems.length === 0 || submitting
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                {submitting ? 'Submitting...' : `Submit Dispatch (${scannedItems.length} items)`}
              </button>
            </div>
          </div>
        </ReusableSidebar>
      </div>
    </div>
  );
};

export default DispatchScreen;
